import React, { forwardRef, useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";

import Select from "react-select";

import colors from "../../assets/theme/base/colors";

import styles from "./styles";
import SoftBox from "../SoftBox";
import { useGetMerchants } from "./hooks";
import _ from "lodash";
import { Typography } from "@mui/material";
import { getMerchant, setMerchant } from "../../utils/localStorage";

/** Presents merchant choices using the reference workspace tile and account label. */
const MerchantSelect = forwardRef(
  ({ size = "medium", error = false, success = false, ...rest }, ref) => {
    const { light } = colors;
    const merchant = getMerchant();
    const [selectedMerchant, setSelectedMerchant] = useState(merchant);
    // store
    // hooks
    const { data: merchants } = useGetMerchants();
    const options = useMemo(() => {
      return _.map(merchants, (merchant) => ({
        value: merchant._id,
        label: (
          <SoftBox display="flex" alignItems="center" gap="10px" minWidth={0}>
            <SoftBox
              aria-hidden="true"
              sx={({ palette }) => ({
                display: "grid",
                placeItems: "center",
                width: 34,
                height: 34,
                flexShrink: 0,
                border: "1px solid " + palette.grey[300],
                borderRadius: "9px",
                backgroundColor: palette.surface.alternate,
                color: palette.dark.main,
                fontSize: "14px",
                fontWeight: 750,
              })}
            >
              {merchant.name?.trim().charAt(0).toUpperCase() || "M"}.
            </SoftBox>
            <SoftBox minWidth={0}>
              <Typography
                component="span"
                noWrap
                sx={({ typography }) => ({
                  display: "block",
                  fontSize: typography.size.xs,
                  fontWeight: 600,
                  lineHeight: 1.4,
                })}
              >
                {merchant.name}
              </Typography>
              <Typography
                component="span"
                sx={({ palette }) => ({
                  display: "block",
                  marginTop: "2px",
                  fontSize: "11px",
                  lineHeight: 1.4,
                  color: palette.text.main,
                })}
              >
                Merchant account
              </Typography>
            </SoftBox>
          </SoftBox>
        ),
      }));
    }, [merchants]);

    // functions

    const onChange = (selectedOption) => {
      const selectedMerchant = _.find(merchants, { _id: selectedOption.value });
      setMerchant(selectedMerchant);
      setSelectedMerchant(selectedMerchant);
      window.location.reload();
    };

    useEffect(() => {
      // local storage is empty
      if (!merchant?._id && merchants && merchants.length > 0) {
        setMerchant(merchants[0]);
        setSelectedMerchant(merchants[0]);
      }

      // local storage is not empty but contains non-exist merchant id
      if (merchant?._id && merchants && merchants.length > 0) {
        const found = _.find(merchants, { _id: merchant?._id });
        if (!found) {
          setMerchant(merchants[0]);
          setSelectedMerchant(merchants[0]);
        }
      }
    }, [merchants, merchant]);
    if (!options || options.length === 0) return null;
    return (
      <SoftBox px={2}>
        <Select
          options={options}
          {...rest}
          ref={ref}
          isDisabled={options.length === 1 || rest.isDisabled}
          styles={styles(size, error, success)}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary25: light.main,
              primary: light.main,
            },
          })}
          onChange={onChange}
          value={options.find((option) => option?.value === selectedMerchant._id)}
        />
      </SoftBox>
    );
  }
);

MerchantSelect.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  error: PropTypes.bool,
  success: PropTypes.bool,
};

export default MerchantSelect;
